<template>
  <model-form
    ref="modelForm"
    :model="tipShareRule"
    modal-type="modal"
  >
    <div class="column">
      <q-tabs
        v-model="tab"
        align="justify"
        dense
      >
        <q-tab
          name="details"
          label="Details"
        />
        <q-tab
          name="restrictions"
          label="Restrictions"
        />
      </q-tabs>

      <div class="row q-col-gutter-sm items-center q-mt-sm">
        <model-form-input
          field="name"
          class="col-12"
          label="Name"
          autofocus
          :rules="[val => !!val || 'Field is required']"
        />
      </div>

      <q-tab-panels v-model="tab">
        <q-tab-panel
          name="details"
          class="q-pa-none"
        >
          <div class="column">
            <div class="row q-col-gutter-sm items-center">
              <model-form-select
                field="tipOutSource"
                class="col-6"
                label="Tip Out Source"
                :options="$mh.asOptions(SOURCES.ALL)"
                map-options
                emit-value
                :rules="[val => !!val || 'Field is required']"
              >
                <template #option="scope">
                  <q-item
                    v-bind="scope.itemProps"
                    v-on="scope.itemEvents"
                  >
                    <q-item-section>
                      <div class="column">
                        <span
                          class="text-body-em"
                          style="font-size: 0.9rem;"
                        >{{ scope.opt.label }}</span>
                        <span class="text-caption">{{ SOURCES_TOOLTIPS[scope.opt.value] }}</span>
                      </div>
                    </q-item-section>
                  </q-item>
                </template>
                <q-tooltip
                  anchor="top middle"
                  self="bottom middle"
                >
                  The Tip Out amount will be calculated from the selected source.
                </q-tooltip>
              </model-form-select>
              <model-form-input
                field="tipOutPercentageThousandths"
                class="col-6"
                label="Tip Out Percentage"
                :decode="val => val / 1000"
                :encode="val => val * 1000"
                suffix="%"
                type="number"
                min="0.01"
                max="100"
                step="5"
                :rules="[val => val !== null || 'Field is required']"
                bottom-slots
                @update:model-value="value => tipShareRule.tipOutPercentageThousandths = value * 1000"
              />
            </div>
          </div>
          <div class="row q-col-gutter-sm items-center">
            <tip-pool-share-table
              ref="tipPoolShareTable"
              class="col-12 q-mb-md"
              :tip-share-rule="tipShareRule"
            />
          </div>
          <div
            v-if="!$_.isEmpty(SOURCE_CONDITIONS[tipShareRule.tipOutSource])"
            class="row q-col-gutter-sm items-center"
          >
            <model-form-select
              field="tipOutSourceConditions"
              class="col-12"
              label="Tip Out Conditions"
              :options="$mh.asOptions(SOURCE_CONDITIONS[tipShareRule.tipOutSource])"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :decode="sourceConditions => $mh.objectToArray(sourceConditions)"
              :encode="enabledSourceConditions => $mh.arrayToObject(enabledSourceConditions || [], { allKeys: SOURCE_CONDITIONS.ALL })"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="column">
                      <span
                        class="text-body-em"
                        style="font-size: 0.9rem;"
                      >{{ scope.opt.label }}</span>
                      <span class="text-caption">{{ SOURCE_CONDITIONS_TOOLTIPS[scope.opt.value] }}</span>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
              >
                The Tip Out amount will be calculated while applying the selected conditions.
              </q-tooltip>
            </model-form-select>
            <order-item-types-condition-form
              v-if="tipShareRule.tipOutSourceConditions[SOURCE_CONDITIONS.ORDER_ITEM_TYPES]"
              :conditional="tipShareRule"
              conditions-requirements-field="tipOutSourceConditionsRequirements"
            />
          </div>
        </q-tab-panel>

        <q-tab-panel
          name="restrictions"
          class="q-pa-none"
          style="overflow-y: hidden;"
        >
          <div class="column">
            <model-form-select
              field="conditions"
              label="Conditions"
              :options="$mh.asOptions(CONDITIONS.ALL)"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :decode="conditions => $mh.objectToArray(conditions)"
              :encode="enabledConditions => $mh.arrayToObject(enabledConditions || [], { allKeys: CONDITIONS.ALL })"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="column">
                      <span
                        class="text-body-em"
                        style="font-size: 0.9rem;"
                      >{{ scope.opt.label }}</span>
                      <span class="text-caption">{{ CONDITIONS_TOOLTIPS[scope.opt.value] }}</span>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
              >
                The Tip Share Rule will only apply when the selected conditions are met.
              </q-tooltip>
            </model-form-select>
            <div class="row q-col-gutter-sm items-center">
              <price-condition-form
                v-if="tipShareRule.conditions[CONDITIONS.AMOUNT]"
                :conditional="tipShareRule"
                :exclude-price-field="CONDITIONS_REQUIREMENTS.EXCLUDE_AMOUNT"
                :min-price-field="CONDITIONS_REQUIREMENTS.MIN_AMOUNT"
                :max-price-field="CONDITIONS_REQUIREMENTS.MAX_AMOUNT"
              />
              <order-item-types-condition-form
                v-if="tipShareRule.conditions[CONDITIONS.ORDER_ITEM_TYPES]"
                :conditional="tipShareRule"
              />
              <delivery-types-condition-form
                v-if="tipShareRule.conditions[CONDITIONS.DELIVERY_TYPES]"
                :conditional="tipShareRule"
              />
              <delivery-locations-condition-form
                v-if="tipShareRule.conditions[CONDITIONS.DELIVERY_LOCATIONS]"
                :conditional="tipShareRule"
              />
              <locations-condition-form
                v-if="tipShareRule.conditions[CONDITIONS.LOCATIONS]"
                :conditional="tipShareRule"
              />
              <location-groups-condition-form
                v-if="tipShareRule.conditions[CONDITIONS.LOCATION_GROUPS]"
                :conditional="tipShareRule"
              />
            </div>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </div>
  </model-form>
</template>

<script>
import { CONDITIONS, CONDITIONS_REQUIREMENTS, SOURCE_CONDITIONS, SOURCE_CONDITIONS_REQUIREMENTS, SOURCES } from 'src/models/tip/TipShareRule'
import DeliveryLocationsConditionForm from 'src/modules/admin/components/condition-requirement-forms/DeliveryLocationsConditionForm'
import DeliveryTypesConditionForm from 'src/modules/admin/components/condition-requirement-forms/DeliveryTypesConditionForm'
import LocationGroupsConditionForm from 'src/modules/admin/components/condition-requirement-forms/LocationGroupsConditionForm'
import LocationsConditionForm from 'src/modules/admin/components/condition-requirement-forms/LocationsConditionForm'
import OrderItemTypesConditionForm from 'src/modules/admin/components/condition-requirement-forms/OrderItemTypesConditionForm'
import PriceConditionForm from 'src/modules/admin/components/condition-requirement-forms/PriceConditionForm'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import TipPoolShareTable from 'src/modules/admin/tip/TipPoolShareTable'

const SOURCES_TOOLTIPS = {
  [SOURCES.TIP]: 'Tip Out amount will be calculated from the tip amount.',
  [SOURCES.ORDER_SUBTOTAL]: 'Tip Out amount will be calculated from the order subtotal, after redemptions and before taxes.',
  [SOURCES.ORDER_TOTAL]: 'Tip Out amount will be calculated from the order total, after redemptions and taxes.'
}

const SOURCE_CONDITIONS_TOOLTIPS = {
  [SOURCE_CONDITIONS.ORDER_ITEM_TYPES]: 'Tip Out will be calculated only using order items of matching types.'
}

const CONDITIONS_TOOLTIPS = {
  [CONDITIONS.AMOUNT]: 'Tip Share Rule will only apply on Tips meeting the given price range.',
  [CONDITIONS.ORDER_ITEM_TYPES]: 'Tip Share Rule will only apply on Tips from orders with ANY order items matching the given types.',
  [CONDITIONS.DELIVERY_TYPES]: 'Tip Share Rule will only apply on Tips from orders with ANY delivery types matching the given types.',
  [CONDITIONS.DELIVERY_LOCATIONS]: 'Tip Share Rule will only apply on Tips from orders with ANY delivery locations matching the given locations.',
  [CONDITIONS.LOCATIONS]: 'Tip Share Rule will only apply on Tips from orders with ANY locations matching the given locations.',
  [CONDITIONS.LOCATION_GROUPS]: 'Tip Share Rule will only apply on Tips from orders with ANY location groups matching the given location groups.'
}

export default {
  name: 'TipShareRuleForm',
  components: {
    ModelForm,
    PriceConditionForm,
    OrderItemTypesConditionForm,
    DeliveryTypesConditionForm,
    DeliveryLocationsConditionForm,
    LocationsConditionForm,
    LocationGroupsConditionForm,
    TipPoolShareTable
  },
  models: {
    tipShareRule: {
      editing: true,
      tipPoolShares: {
        tipPoolType: {
          fields: ['id', 'name']
        }
      },
      deliveryLocations: {
        fields: ['id', 'name']
      },
      locations: {
        fields: ['id', 'name']
      },
      locationGroups: {
        fields: ['id', 'name']
      }
    }
  },
  data () {
    return {
      tab: 'details'
    }
  },
  watch: {
    'tipShareRule.tipOutSource' () {
      const excludedSourceConditions = _.without(SOURCE_CONDITIONS.ALL, ...SOURCE_CONDITIONS[this.tipShareRule.tipOutSource])

      _.each(excludedSourceConditions, sourceCondition => { this.tipShareRule.tipOutSourceConditions[sourceCondition] = false })
    }
  },
  created () {
    _.extend(this, {
      CONDITIONS,
      CONDITIONS_TOOLTIPS,
      CONDITIONS_REQUIREMENTS,
      SOURCES,
      SOURCES_TOOLTIPS,
      SOURCE_CONDITIONS,
      SOURCE_CONDITIONS_TOOLTIPS,
      SOURCE_CONDITIONS_REQUIREMENTS
    })
  },
  methods: {
    validate () {
      this.$refs.tipPoolShareTable.validate()
    },
    reset () {
      this.$refs.tipPoolShareTable.resetValidation()
    }
  }
}
</script>
